import type { CSSProperties } from 'react';
import { Text, Tooltip } from '@usealloy/component-library';
import styled from 'styled-components';
import { accessTypeLabelMap } from '~/dashboard/admin/provisioning/helpers/constants';
import { RBACAccessType } from '~/dashboard/admin/provisioning/types';
import { handleNavigation } from '~/dashboard/admin/provisioning/helpers/handleCustomerNavigation';
import log from '~/helpers/logging';
import { AccessBadge } from './AccessBadge';
import { HIGHLIGHT_BACKGROUND } from './constants';
import type { AllCustomersRow } from './types';

const REQUEST_ACCESS_LABEL = accessTypeLabelMap[RBACAccessType.None];
const PENDING_LABEL = accessTypeLabelMap[RBACAccessType.Pending];

const RowContainer = styled('div')<{ clickable?: boolean; highlighted?: boolean }>(({
  clickable, highlighted,
}) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '8px',
  width: '100%',
  padding: '6px 12px',
  boxSizing: 'border-box',
  cursor: clickable ? 'pointer' : 'default',
  background: highlighted ? HIGHLIGHT_BACKGROUND : undefined,
  '&:hover': clickable ? { background: HIGHLIGHT_BACKGROUND } : {},
}));

const OrgInfo = styled('div')({
  overflow: 'hidden',
  minWidth: 0,
});

const OrgNameText = styled(Text)({
  display: 'block',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

const CellText = styled(Text)({
  display: 'block',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  color: 'var(--cl-gray-800)',
});

/**
 * Per-row click/keyboard-activation routing, extracted as a pure(ish) side
 * effect function so it can be triggered identically from a mouse click
 * (`CustomerRow`'s own `onClick`) and from keyboard activation (`Enter` in
 * `NavList.customerSwitcher.tsx`, which only has `row` data — not a mounted
 * `CustomerRow` instance, since `react-virtualized` only mounts visible rows).
 *
 * - has active access (Permanent/Temporary, or any row for an AFEF parent agent,
 *   see `getTableData`'s `is_active`) -> switch context directly via
 *   `handleNavigation`, same call `useHandleRowClick` uses for the admin table.
 * - no access ("Request Access") -> mirrors the three-way branch in
 *   `getTableColumns-AlloyAdmin.tsx`'s access_type column: same-cell opens
 *   the inline modal (bubbled via `onRequestAccess`); same-TLD cross-cell
 *   bounces to the target cell's admin customers page (picked up there by its
 *   own `access_modal`/`cust_id` query-param effect); cross-TLD redirects
 *   through the vanity-domain login flow.
 * - pending, or no resolved `customer_id` -> no-op.
 */
export function activateCustomerRow(
  row: AllCustomersRow,
  { currentCellKey, onRequestAccess }: {
    currentCellKey: string;
    onRequestAccess: (row: AllCustomersRow) => void;
  },
) {
  if (row.is_active) {
    navigateToCustomer({
      currentCell: currentCellKey,
      cellHostUrl: row.app?.url,
      targetCell: row.cellKey,
      path: `/admin/customers/${row.customer_id}/session`,
    });
    return;
  }

  if (row.access_type !== REQUEST_ACCESS_LABEL) return;
  if (!row.customer_id) return;

  // Compared on cell identity, not `app.base_url === window.location.origin` as
  // the legacy admin table does. `app.base_url` is the cell's canonical
  // provisioning-registered URL, which is *not* the browsing origin in QA or any
  // ephemeral env — so an origin match never fires there and every row, including
  // customers in the cell the agent is already logged into, bounces cross-cell
  // instead of opening the modal. The cell key is what the branch actually means.
  if (row.cellKey === currentCellKey) {
    onRequestAccess(row);
    return;
  }

  // Only the cross-cell branches below need a routable URL for the target cell.
  if (!row.app?.url || !row.app?.base_url) return;

  const currentTLD = window.location.host.split('.').pop();
  const routedTLD = row.app.base_url.split('.').pop();

  if (currentTLD !== routedTLD) {
    // handle .co vs .com rbac redirect, mirrors getTableColumns-AlloyAdmin.tsx
    const encodedVanityUrl = `vanity_url=${encodeURIComponent(row.app.base_url)}`
      + `/v3/dashboard/admin/customers/?access_modal=true&cust_id=${row.customer_id}`;
    window.location.replace(`${row.app.base_url}/v3/dashboard/login?${encodedVanityUrl}`);
  } else {
    // authenticated cell-to-cell nav (same TLD): open the modal on the target cell
    navigateToCustomer({
      currentCell: currentCellKey,
      cellHostUrl: row.app.base_url,
      targetCell: row.cellKey,
      path: `/v3/dashboard/admin/customers/?access_modal=true&cust_id=${row.customer_id}`,
    });
  }
}

/**
 * `handleNavigation`, with its rejection handled.
 *
 * The cross-cell branch of `handleNavigation` fetches a JWT and posts it to the
 * target cell, either of which can reject (offline, CORS, the cell being down).
 * Every existing caller — `useHandleRowClick`, `CustomerListView`,
 * `getTableColumns-AlloyAdmin` — drops that promise on the floor, so a failure
 * surfaces only as an unhandled rejection. Log it instead; `handleNavigation`
 * already logs the authenticated-but-rejected case itself.
 */
async function navigateToCustomer(params: Parameters<typeof handleNavigation>[0]) {
  try {
    await handleNavigation(params);
  } catch (error) {
    log('error', 'Error navigating to customer from the sidebar customer switcher', {}, error);
  }
}

export interface CustomerRowProps {
  row: AllCustomersRow;
  style: CSSProperties;
  /** current cell key, e.g. `alloyEnvironment.replace('kube', '')` - needed to decide same-cell vs cross-cell nav */
  currentCellKey: string;
  onRequestAccess: (row: AllCustomersRow) => void;
  /** True when this row is the currently keyboard-highlighted item */
  isHighlighted?: boolean;
  /** Moves the switcher's shared highlight onto this row when the pointer enters it */
  onHover?: () => void;
}

/** A single row in the "All Customers" virtualized list. */
export const CustomerRow = ({
  row, style, currentCellKey, onRequestAccess, isHighlighted, onHover,
}: CustomerRowProps) => {
  const isPending = row.access_type === PENDING_LABEL;
  const canRequestAccess = row.access_type === REQUEST_ACCESS_LABEL;
  const clickable = row.is_active || canRequestAccess;

  const handleClick = () => {
    /* v8 ignore next -- Defensive: onClick is only attached to a clickable row */
    if (!clickable) return;
    activateCustomerRow(row, { currentCellKey, onRequestAccess });
  };

  const content = (
    <RowContainer
      style={style}
      clickable={clickable}
      highlighted={isHighlighted}
      onClick={clickable ? handleClick : undefined}
      onMouseEnter={onHover}
    >
      <OrgInfo>
        <OrgNameText size={2}>{row.organization_name}</OrgNameText>
        <CellText size={1}>{row.cell}</CellText>
      </OrgInfo>
      <AccessBadge accessLabel={row.access_type} />
    </RowContainer>
  );

  return isPending ? (
    <Tooltip content="Access request pending approval" side="right">
      {content}
    </Tooltip>
  ) : content;
};
